import type { KkmHandshakeReady, KkmServerAdapter } from '@/shared/api';

import {
  type ConfirmShiftKktInput,
  type OpenShiftInput,
  type ShiftFieldsFragment,
  ShiftStatus,
} from '@shared/api/graphql/gql/graphql';

import type { KktOpeningProof } from './fiscal-shift-actions';

import { awaitOpenSessionProof } from './fiscal-operation-session';
import { executeFiscalShiftAction, proofFromOpenSession, proofFromShiftCommand } from './fiscal-shift-actions';

type ShiftOpeningAdapter = Pick<KkmServerAdapter, 'closeShift' | 'handshake' | 'openShift'>;

export interface ShiftOpeningServices {
  abort: (shiftId: string) => Promise<ShiftFieldsFragment>;
  confirmKkt: (input: ConfirmShiftKktInput) => Promise<ShiftFieldsFragment>;
  open: (input: OpenShiftInput) => Promise<ShiftFieldsFragment>;
}

export interface OpenCashShiftRequest {
  cashierName: string;
  cashierVatin?: string | null;
  confirmExistingKktShift?: (sessionState: 2 | 3) => Promise<boolean>;
  /** Касса из привязки рабочего места: смена не открывается на чужой ККТ. */
  expectedKktDeviceId?: string | null;
  fiscalizationEnabled: boolean;
  locationId: string;
  openingCash: number;
}

export type OpenCashShiftResult = { kind: 'cancelled'; shift: null } | { kind: 'opened'; shift: ShiftFieldsFragment };

export interface ContinueCashShiftOpeningRequest {
  cashierName: string;
  cashierVatin?: string | null;
  expectedKktDeviceId?: string | null;
  shift: ShiftFieldsFragment;
}

export class ShiftOpeningController {
  public constructor(
    private readonly adapter: ShiftOpeningAdapter,
    private readonly services: ShiftOpeningServices,
  ) {}

  public async open(request: OpenCashShiftRequest): Promise<OpenCashShiftResult> {
    assertCashAmount(request.openingCash, 'Укажите наличные в кассе на начало смены.');

    if (!request.fiscalizationEnabled) {
      return {
        kind: 'opened',
        shift: await this.services.open({ locationId: request.locationId, openingCash: request.openingCash }),
      };
    }

    const initialHandshake = requireReady(
      await this.adapter.handshake({ kktDeviceId: request.expectedKktDeviceId ?? undefined }),
    );
    const sessionState = initialHandshake.kkt.sessionState;

    if (sessionState !== 1 && sessionState !== 2 && sessionState !== 3) {
      throw new Error('Касса не сообщила состояние смены. Свяжитесь с управляющим.');
    }

    if ((sessionState === 2 || sessionState === 3) && !(await request.confirmExistingKktShift?.(sessionState))) {
      return { kind: 'cancelled', shift: null };
    }

    const shift = await this.services.open({ locationId: request.locationId, openingCash: request.openingCash });

    let action: Awaited<ReturnType<typeof executeFiscalShiftAction>>;

    try {
      action = await executeFiscalShiftAction(
        this.adapter,
        sessionState === 1 ? 'open' : 'reopen',
        request.cashierName,
        shift.cashier_vatin ?? request.cashierVatin,
        { handshake: initialHandshake },
      );
    } catch (error) {
      await this.abortAfterKktFailure(shift.id);
      throw error;
    }

    const confirmed = await this.confirmOpening(
      shift.id,
      request.cashierName,
      initialHandshake,
      proofFromShiftCommand(initialHandshake, action.openShift),
    );

    return { kind: 'opened', shift: confirmed };
  }

  public async continueOpening(request: ContinueCashShiftOpeningRequest): Promise<ShiftFieldsFragment> {
    if (request.shift.status !== ShiftStatus.Opening) {
      return request.shift;
    }

    const handshake = requireReady(
      await this.adapter.handshake({ kktDeviceId: request.expectedKktDeviceId ?? undefined }),
    );
    const sessionState = handshake.kkt.sessionState;

    // Смена уже открыта на кассе — это чтение и есть доказательство, команду слать незачем.
    if (sessionState === 2) {
      return this.confirmOpening(request.shift.id, request.cashierName, handshake, proofFromOpenSession(handshake));
    }

    if (sessionState !== 1 && sessionState !== 3) {
      throw new Error('Касса не сообщила состояние смены. Свяжитесь с управляющим.');
    }

    const action = await executeFiscalShiftAction(
      this.adapter,
      sessionState === 1 ? 'open' : 'reopen',
      request.cashierName,
      request.cashierVatin,
      { handshake },
    );

    return this.confirmOpening(
      request.shift.id,
      request.cashierName,
      handshake,
      proofFromShiftCommand(handshake, action.openShift),
    );
  }

  public cancelOpening(shiftId: string): Promise<ShiftFieldsFragment> {
    return this.services.abort(shiftId);
  }

  /**
   * Открытие считается подтверждённым, когда есть доказательство: `SessionNumber` в ответе
   * команды либо чтение `GetDataKKT` с `sessionState === 2`. Без доказательства уходим в
   * опрос с ретраями — касса обновляет `SessionState` позже, чем печатает документ.
   */
  private async confirmOpening(
    shiftId: string,
    cashierName: string,
    handshake: KkmHandshakeReady,
    proof: KktOpeningProof | null,
  ): Promise<ShiftFieldsFragment> {
    if (proof !== null) {
      return this.services.confirmKkt({ ...proof, shiftId });
    }

    console.warn('[cash-shift.open-confirm-fallback]', { shiftId });

    const polledProof = await awaitOpenSessionProof(this.adapter, handshake.kktDevice.idDevice);

    if (polledProof === null) {
      throw new Error(
        `Открытие смены для ${cashierName.trim() || 'кассира'} не подтверждено кассой. Нажмите «Продолжить».`,
      );
    }

    return this.services.confirmKkt({ ...polledProof, shiftId });
  }

  private async abortAfterKktFailure(shiftId: string): Promise<void> {
    try {
      await this.services.abort(shiftId);
    } catch {
      // Неудача compensating mutation не должна скрыть исходную ошибку KKT.
      console.error('[cash-shift.opening-abort-failed]', { shiftId });
    }
  }
}

function requireReady(handshake: Awaited<ReturnType<ShiftOpeningAdapter['handshake']>>): KkmHandshakeReady {
  if (handshake.kind !== 'ready') {
    throw new Error(handshake.instruction);
  }

  return handshake;
}

function assertCashAmount(value: number, message: string): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(message);
  }
}
